/**
 * Form submission functionality module
 */
import Tracking from './tracking.js';
import { TRACKING_EVENTS } from './tracking-constants.js';

const FormSubmission = {
    /**
     * Initialize form submission handling
     */
    init() {
        // Handle all forms with data-form-type attribute
        const forms = document.querySelectorAll('form[data-form-type]');
                
        forms.forEach(form => {
            if (form.hasAttribute('data-membership-checkout') || form.hasAttribute('data-reserve-form')) {
                return;
            }
            this.setupFormHandler(form);
        });
    },

    /**
     * Setup form handler for a specific form
     */
    setupFormHandler(form) {
        const formType = form.getAttribute('data-form-type');
        
        form.addEventListener('submit', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.handleFormSubmission(form, formType);
            return false;
        });

        // Add real-time validation
        const inputs = form.querySelectorAll('input[required], textarea[required]');
        inputs.forEach(input => {
            const isCheckbox = input.type === 'checkbox';
            
            if (isCheckbox) {
                input.addEventListener('change', () => {
                    this.validateField(input);
                });
            } else {
                input.addEventListener('blur', () => {
                    this.validateField(input);
                });

                input.addEventListener('input', () => {
                    // Clear validation on input
                    input.classList.remove('invalid');
                    this.clearFieldError(input);
                });
            }
        });

    },

    /**
     * Handle form submission
     */
    handleFormSubmission(form, formType) {
        // Validate form before submission
        if (!this.validateForm(form)) {
            return;
        }

        const submitButton = form.querySelector('button[type="submit"]');
        const submitText = submitButton?.querySelector('.submit-text');
        const submitLoading = submitButton?.querySelector('.submit-loading');
        const messageDiv = form.querySelector('.form-message') || this.createMessageDiv(form);

        // Show loading state
        this.setLoadingState(submitButton, submitText, submitLoading, true);
        this.hideMessage(messageDiv);

        // Prepare form data
        const formData = new FormData(form);
        formData.append('action', 'submit_form');
        
        // ENSURE form_type is always set correctly
        if (formType && formType !== 'undefined' && formType !== '') {
            formData.append('form_type', formType);
        } else {
            // Fallback: detect from form ID or class
            if (form.id === 'newsletter-form' || form.classList.contains('newsletter-banner__form')) {
                formData.append('form_type', 'newsletter');
            } else if (form.id === 'provider-form' || form.classList.contains('lp-form-block__form')) {
                formData.append('form_type', 'provider');
            }
        }
        
        // Use the global AJAX data if available, otherwise fallback
        const ajaxUrl = typeof pe_mp_form_ajax !== 'undefined' ? pe_mp_form_ajax.ajax_url : '/wp-admin/admin-ajax.php';
        const nonce = typeof pe_mp_form_ajax !== 'undefined' ? pe_mp_form_ajax.nonce : form.querySelector('[name="form_nonce"]')?.value;

        if (nonce) {
            formData.set('form_nonce', nonce);
        }

        // Submit form via AJAX
        fetch(ajaxUrl, {
            method: 'POST',
            body: formData,
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
            }
        })
        .then(response => {
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
        })
        .then(data => {
            // Reset loading state
            this.setLoadingState(submitButton, submitText, submitLoading, false);

            if (data.success) {
                this.showSuccessMessage(messageDiv, data.data.message);
                
                // Track successful form submission
                Tracking.trackStandardEvent(TRACKING_EVENTS.FORMS.FORM_SUBMITTED, {
                    form_type: formType,
                    success: true
                });

                // Update vote counts for editor_question form
                if (formType === 'editor_question') {
                    this.updateQuestionVotes(form);
                }

                // Reset form on success
                form.reset();

                const joinModal = form.closest('[data-join-psystandard-modal]');
                if (joinModal && typeof window.showJoinPsyStandardSuccess === 'function') {
                    window.showJoinPsyStandardSuccess(joinModal);
                }

                const openMembership = form.closest('[data-membership-join-modal="open"]');
                if (openMembership && typeof window.showMembershipOpenSuccess === 'function') {
                    window.showMembershipOpenSuccess(openMembership);
                }

                if (formType === 'homepage-early-access' && !joinModal && !openMembership) {
                    const applyJoinModal = document.querySelector('[data-apply-join-modal].is-open');
                    const membershipModal = document.querySelector('.membership-join.is-open');
                    const openModal = applyJoinModal || membershipModal;
                    if (openModal) {
                        setTimeout(() => {
                            if (typeof window.closeMembershipJoinModals === 'function' && membershipModal) {
                                window.closeMembershipJoinModals();
                            } else {
                                openModal.classList.remove('is-open');
                                openModal.setAttribute?.('hidden', '');
                                document.body.style.overflow = '';
                            }
                        }, 2500);
                    }
                }

                // // Close popup if form is inside a popup
                const popup = form.closest('.popup');
                if (popup) {
                    setTimeout(() => {
                        if (window.PopupManager) {
                            window.PopupManager.closeAllPopups();
                        }
                    }, 2000);
                }

                // Redirect if specified
                if (data.data.redirect) {
                    setTimeout(() => {
                        window.location.href = data.data.redirect;
                    }, 2000);
                }
            } else {
                this.showErrorMessage(messageDiv, data.data.message);
                
                // Track failed form submission
                Tracking.trackStandardEvent(TRACKING_EVENTS.FORMS.FORM_SUBMITTED, {
                    form_type: formType,
                    success: false,
                    error_message: data.data.message
                });
            }
        })
        .catch(error => {
            console.error('Form submission error:', error);
            
            // Reset loading state
            this.setLoadingState(submitButton, submitText, submitLoading, false);
            
            // Provide more specific error messages
            let errorMessage = 'Something went wrong. Please try again.';
            if (error.message.includes('HTTP error! status: 500')) {
                errorMessage = 'Server error occurred. Please try again later.';
            } else if (error.message.includes('Unexpected token')) {
                errorMessage = 'Invalid response from server. Please try again.';
            }
            
            this.showErrorMessage(messageDiv, errorMessage);
            
            // Track network error
            Tracking.trackStandardEvent(TRACKING_EVENTS.FORMS.FORM_SUBMITTED, {
                form_type: formType,
                success: false,
                error_type: 'network_error',
                error_message: error.message
            });
        });
    },

    /**
     * Validate entire form
     */
    validateForm(form) {
        const requiredFields = form.querySelectorAll('input[required], textarea[required]');
        let isValid = true;

        requiredFields.forEach(field => {
            if (!this.validateField(field)) {
                isValid = false;
            }
        });


        return isValid;
    },

    /**
     * Validate individual field
     */
    validateField(field) {
        // Handle checkboxes differently
        const isCheckbox = field.type === 'checkbox';
        const value = isCheckbox ? (field.checked ? '1' : '') : field.value.trim();
        let isValid = true;
        let errorMessage = '';

        // Required field validation
        if (field.hasAttribute('required')) {
            if (isCheckbox && !field.checked) {
                isValid = false;
                errorMessage = 'This field is required';
            } else if (!isCheckbox && !value) {
                isValid = false;
                errorMessage = 'This field is required';
            }
        }

        // Email validation
        if (field.type === 'email' && value) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
                isValid = false;
                errorMessage = 'Please enter a valid email address';
            }
        }

        // Telephone validation
        if (field.type === 'tel' && value) {
            // Allow international formats: +, digits, spaces, dashes, parentheses, dots
            // Must contain at least 5 digits
            const phoneRegex = /^[\+]?[0-9\s\-\(\)\.]{5,}$/;
            const digitCount = (value.match(/\d/g) || []).length;
            
            if (!phoneRegex.test(value) || digitCount < 5) {
                isValid = false;
                errorMessage = 'Please enter a valid phone number';
            }
        }

        // Length validation
        if (field.hasAttribute('maxlength')) {
            const maxLength = parseInt(field.getAttribute('maxlength'));
            if (value.length > maxLength) {
                isValid = false;
                errorMessage = `Maximum ${maxLength} characters allowed`;
            }
        }

        // Update field appearance
        if (isValid) {
            field.classList.remove('invalid');
            this.clearFieldError(field);
            
            // Clear acceptance checkbox validation state
            if (isCheckbox) {
                const checkboxContainer = field.closest('.acceptance-checkbox');
                if (checkboxContainer) {
                    checkboxContainer.classList.remove('acceptance-checkbox--required');
                }
            }
        } else {
            field.classList.add('invalid');
            this.showFieldError(field, errorMessage);
            
            // Add acceptance checkbox validation state
            if (isCheckbox) {
                const checkboxContainer = field.closest('.acceptance-checkbox');
                if (checkboxContainer) {
                    checkboxContainer.classList.add('acceptance-checkbox--required');
                }
            }
        }

        return isValid;
    },

    /**
     * Show field error message
     */
    showFieldError(field, message) {
        this.clearFieldError(field);
        
        const errorDiv = document.createElement('div');
        errorDiv.className = 'field-message field-message--error';
        errorDiv.textContent = message;
        
        // Handle checkbox wrapper case
        if (field.classList && field.classList.contains('checkbox__wrapper')) {
            field.appendChild(errorDiv);
        } else if (field.type === 'checkbox') {
            // Handle acceptance checkbox
            const wrapper = field.closest('.acceptance-checkbox') || field.parentNode;
            wrapper.appendChild(errorDiv);
        } else {
            // Handle regular input fields
            const wrapper = field.closest('.input-v2__wrapper') || field.parentNode;
            wrapper.appendChild(errorDiv);
        }
    },

    /**
     * Clear field error message
     */
    clearFieldError(field) {
        // Handle checkbox wrapper case
        if (field.classList && field.classList.contains('checkbox__wrapper')) {
            const existingError = field.querySelector('.field-message--error');
            if (existingError) {
                existingError.remove();
            }
        } else if (field.type === 'checkbox') {
            // Handle acceptance checkbox
            const wrapper = field.closest('.acceptance-checkbox') || field.parentNode;
            const existingError = wrapper.querySelector('.field-message--error');
            if (existingError) {
                existingError.remove();
            }
        } else {
            // Handle regular input fields
            const wrapper = field.closest('.input-v2__wrapper') || field.parentNode;
            const existingError = wrapper.querySelector('.field-message--error');
            if (existingError) {
                existingError.remove();
            }
        }
    },

    /**
     * Set loading state for submit button
     */
    setLoadingState(button, textElement, loadingElement, isLoading) {
        if (!button) return;

        button.disabled = isLoading;
        
        if (textElement) {
            textElement.style.display = isLoading ? 'none' : 'inline';
        }
        
        if (loadingElement) {
            loadingElement.style.display = isLoading ? 'inline' : 'none';
        }
    },

    /**
     * Create message div if it doesn't exist
     */
    createMessageDiv(form) {
        const messageDiv = document.createElement('div');
        messageDiv.className = 'form-message';
        messageDiv.style.display = 'none';
        
        // Insert at the beginning of the form
        form.insertBefore(messageDiv, form.firstChild);
        
        return messageDiv;
    },

    /**
     * Show success message
     */
    showSuccessMessage(messageDiv, message) {
        messageDiv.className = 'form-message alert alert--success';
        messageDiv.innerHTML = `${message}`;
        messageDiv.style.display = 'block';
        
        // Auto-hide after 5 seconds
        setTimeout(() => {
            this.hideMessage(messageDiv);
        }, 5000);
    },

    /**
     * Show error message
     */
    showErrorMessage(messageDiv, message) {
        messageDiv.className = 'form-message alert alert--error';
        messageDiv.innerHTML = `${message}`;
        messageDiv.style.display = 'block';
    },

    /**
     * Hide message
     */
    hideMessage(messageDiv) {
        messageDiv.style.display = 'none';
    },

    /**
     * Update vote counts for editor question answers after submission
     */
    updateQuestionVotes(form) {
        const postIdInput = form.querySelector('input[name="post_id"]');
        if (!postIdInput) {
            return;
        }

        const postId = postIdInput.value;
        const ajaxUrl = typeof pe_mp_form_ajax !== 'undefined' ? pe_mp_form_ajax.ajax_url : '/wp-admin/admin-ajax.php';
        const nonce = typeof pe_mp_form_ajax !== 'undefined' ? pe_mp_form_ajax.nonce : '';

        const formData = new FormData();
        formData.append('action', 'update_question_votes');
        formData.append('post_id', postId);
        if (nonce) {
            formData.append('nonce', nonce);
        }

        fetch(ajaxUrl, {
            method: 'POST',
            body: formData,
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
            }
        })
        .then(response => response.json())
        .then(data => {
            if (data.success && data.data.votes) {
                // Calculate total votes
                const votes = data.data.votes;
                const totalVotes = Object.values(votes).reduce((sum, count) => sum + parseInt(count, 10), 0);

                // Update all question instances for this post
                this.updateAllQuestionInstances(postId, votes, totalVotes);
            }
        })
        .catch(error => {
            console.error('Error updating question votes:', error);
        });
    },

    /**
     * Update all question instances sharing the same post ID
     */
    updateAllQuestionInstances(postId, votes, totalVotes) {
        const questionContainers = document.querySelectorAll(`.article-v2__question[data-question-post-id="${postId}"]`);
        if (!questionContainers.length) {
            return;
        }

        questionContainers.forEach(container => {
            const forms = container.querySelectorAll('.article-v2__question-form');
            const resultsSection = container.querySelector('[data-question-results]');

            // Update inputs with new vote counts
            const inputs = container.querySelectorAll('[data-answer-index]');
            inputs.forEach(input => {
                const answerIndex = input.getAttribute('data-answer-index');
                if (Object.prototype.hasOwnProperty.call(votes, answerIndex)) {
                    input.setAttribute('data-initial-votes', votes[answerIndex]);
                }
            });

            // Update results bars
            this.updateResultsBars(container, votes, totalVotes);

            // Hide forms and show results across all instances
            forms.forEach(instanceForm => {
                instanceForm.style.display = 'none';
            });

            if (resultsSection) {
                resultsSection.style.display = 'flex';
            }
        });
    },

    /**
     * Update the results score bars with new vote data
     */
    updateResultsBars(questionContainer, votes, totalVotes) {
        const resultsBars = questionContainer.querySelectorAll('.scores-bar--subscales');
        
        resultsBars.forEach((bar, index) => {
            const voteKey = index.toString();
            const voteCount = parseInt(votes[voteKey] ?? votes[index] ?? 0, 10) || 0;
            const percentage = totalVotes > 0 ? ((voteCount / totalVotes) * 100).toFixed(1) : 0;
            
            // Update progress fill width
            const progressFill = bar.querySelector('.scores-bar__progress-fill');
            if (progressFill) {
                progressFill.style.width = percentage + '%';
            }
            
            // Update legend score
            const legendScore = bar.querySelector('.scores-bar__legend-score');
            if (legendScore) {
                legendScore.innerHTML = percentage + '%';
            }
        });
    }
};

// Export for ES6 modules
export default FormSubmission;
